/**
 * AnalyticsPage — admin-only. Renders the high-level Codex OS usage summary
 * computed by GET /api/analytics/summary.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    BarChart3,
    MessageSquareText,
    FileText,
    Mic,
    Eye,
    Download,
    Globe,
    Users,
    MapPin,
    Loader2,
} from "lucide-react";
import { analyticsApi } from "../lib/adminApi";
import { getMe } from "../lib/auth";

export default function AnalyticsPage() {
    const [auth, setAuth] = useState("checking");
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const me = await getMe();
                if (!me.is_admin) {
                    setAuth("non-admin");
                    return;
                }
                setAuth("admin");
                const summary = await analyticsApi.summary();
                setData(summary);
            } catch (_e) {
                setAuth("guest");
            }
        })();
    }, []);

    if (auth === "checking") {
        return <Shell><Spinner label="Verifying session" /></Shell>;
    }
    if (auth === "guest") {
        return (
            <Shell>
                <p className="text-[#CBD5E1] text-base">
                    You need to sign in to view analytics.{" "}
                    <Link to="/admin" className="text-[#F9E596] underline">
                        Sign in
                    </Link>
                </p>
            </Shell>
        );
    }
    if (auth === "non-admin") {
        return (
            <Shell>
                <p className="text-[#F43F5E] text-base">
                    This Google account is not on the admin allowlist.
                </p>
            </Shell>
        );
    }
    if (!data) {
        return <Shell><Spinner label="Loading analytics" /></Shell>;
    }

    return (
        <Shell>
            <div className="flex items-center justify-between gap-4">
                <div>
                    <p className="font-mono-os text-xs tracking-[0.36em] uppercase text-[#D4AF37]">
                        Codex Admin
                    </p>
                    <h2 className="mt-2 font-serif-display text-4xl text-[#F9E596] flex items-center gap-2">
                        <BarChart3 size={28} className="text-[#D4AF37]" /> Analytics
                    </h2>
                </div>
                <button
                    onClick={() => navigate("/admin")}
                    data-testid="analytics-back"
                    className="codex-button !px-5 !py-2.5 !text-sm"
                >
                    <ArrowLeft size={15} /> Admin
                </button>
            </div>

            <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Visits (lifetime)" value={data.totals.visits || 0} icon={Globe} accent="#F9E596" />
                <Stat label="Visits (30 days)" value={data.totals.visits_30d || 0} icon={MapPin} accent="#D4AF37" />
                <Stat label="Unique visitors" value={data.totals.unique_visitors || 0} icon={Users} accent="#4ADE80" />
                <Stat label="Questions" value={data.totals.questions} icon={MessageSquareText} />
            </div>

            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Studies" value={data.totals.studies} icon={FileText} />
                <Stat label="Voice calls" value={data.totals.voice_calls} icon={Mic} />
                <Stat label="Video views" value={data.totals.video_views} icon={Eye} />
                <Stat label="Downloads" value={data.totals.study_downloads} icon={Download} />
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
                <TopLocationsCard
                    title="Top countries"
                    rows={(data.top_countries || []).map((r) => ({
                        name: r.country,
                        code: r.country_code,
                        count: r.count,
                    }))}
                    icon={Globe}
                />
                <TopLocationsCard
                    title="Top cities"
                    rows={(data.top_cities || []).map((r) => ({
                        name: r.label,
                        count: r.count,
                    }))}
                    icon={MapPin}
                />
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
                <DailyChart series={data.daily} />
                <TopList
                    title="Top question terms"
                    rows={data.top_questions.map((r) => ({ name: r.term, count: r.count }))}
                />
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
                <TopList
                    title="Most-viewed videos"
                    rows={data.top_videos.map((v) => ({ name: v.title, count: v.view_count }))}
                    suffix="views"
                />
                <TopList
                    title="Most-downloaded studies"
                    rows={data.top_studies.map((s) => ({ name: s.title, count: s.download_count }))}
                    suffix="downloads"
                />
            </div>

            <div className="mt-8">
                <h3 className="font-serif-display text-2xl text-[#F9E596] mb-3 flex items-center gap-2">
                    <MapPin size={18} className="text-[#D4AF37]" /> Recent visits
                </h3>
                <div className="codex-card divide-y divide-[#D4AF37]/15">
                    {(data.recent_visits || []).length === 0 && (
                        <p className="p-4 text-[#CBD5E1] text-base">
                            No visits logged yet. The site logs one visit per browser session.
                        </p>
                    )}
                    {(data.recent_visits || []).map((v) => (
                        <RecentVisitRow key={v.id || v.created_at} v={v} />
                    ))}
                </div>
            </div>

            <div className="mt-8">
                <h3 className="font-serif-display text-2xl text-[#F9E596] mb-3">
                    Recent activity
                </h3>
                <div className="codex-card divide-y divide-[#D4AF37]/15">
                    {data.recent.length === 0 && (
                        <p className="p-4 text-[#CBD5E1] text-base">No activity yet.</p>
                    )}
                    {data.recent.map((ev) => (
                        <RecentRow key={ev.id || ev.created_at + ev.type} ev={ev} />
                    ))}
                </div>
            </div>

            {error && <p className="mt-4 text-[#F43F5E] text-base">{error}</p>}
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-14 py-10 relative z-10">
            <div className="codex-card px-6 sm:px-8 py-8" data-testid="analytics-shell">
                {children}
            </div>
        </div>
    );
}

function Spinner({ label }) {
    return (
        <p className="font-mono-os text-xs tracking-[0.36em] uppercase text-[#D4AF37] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> {label}
        </p>
    );
}

function Stat({ label, value, icon: Icon, accent }) {
    return (
        <div className="codex-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between" style={accent ? { color: accent } : { color: "#D4AF37" }}>
                <Icon size={22} />
                <span className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#94A3B8]">
                    {label}
                </span>
            </div>
            <p
                className="mt-3 font-serif-display text-4xl leading-none"
                style={{ color: accent || "#F9E596" }}
            >
                {value.toLocaleString()}
            </p>
        </div>
    );
}

function TopList({ title, rows, suffix = "" }) {
    return (
        <div className="codex-card p-6">
            <h4 className="font-serif-display text-xl text-[#F9E596] mb-4">{title}</h4>
            {rows.length === 0 ? (
                <p className="text-[#CBD5E1] text-base">No data yet.</p>
            ) : (
                <ol className="space-y-3">
                    {rows.map((r, i) => (
                        <li
                            key={i}
                            className="flex items-center justify-between gap-3 text-base"
                        >
                            <span className="text-[#F1F5F9] truncate">
                                {i + 1}. {r.name}
                            </span>
                            <span className="font-mono-os text-xs tracking-[0.24em] uppercase text-[#D4AF37]">
                                {r.count}
                                {suffix ? ` ${suffix}` : ""}
                            </span>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}

function TopLocationsCard({ title, rows, icon: Icon }) {
    const max = rows.reduce((m, r) => Math.max(m, r.count || 0), 1);
    return (
        <div className="codex-card p-6" data-testid={`locations-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <h4 className="font-serif-display text-xl text-[#F9E596] mb-4 flex items-center gap-2">
                {Icon && <Icon size={18} className="text-[#D4AF37]" />} {title}
            </h4>
            {rows.length === 0 ? (
                <p className="text-[#CBD5E1] text-base">
                    No location data yet. Wait for a few non-admin visits.
                </p>
            ) : (
                <ol className="space-y-3">
                    {rows.map((r, i) => {
                        const pct = Math.max(6, Math.round((r.count / max) * 100));
                        return (
                            <li key={i} className="space-y-1.5">
                                <div className="flex items-center justify-between gap-3 text-base">
                                    <span className="text-[#F1F5F9] truncate flex items-center gap-2">
                                        {r.code ? (
                                            <span
                                                className="font-mono-os text-[10px] tracking-widest uppercase text-[#D4AF37] bg-[#0A1220]/60 border border-[#D4AF37]/30 rounded px-1.5 py-0.5"
                                            >
                                                {r.code}
                                            </span>
                                        ) : null}
                                        {i + 1}. {r.name || "Unknown"}
                                    </span>
                                    <span className="font-mono-os text-xs tracking-[0.24em] uppercase text-[#D4AF37] flex-shrink-0">
                                        {r.count}
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-[#050B14]/60 overflow-hidden">
                                    <span
                                        className="block h-full bg-gradient-to-r from-[#D4AF37] to-[#F9E596]"
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}

function RecentVisitRow({ v }) {
    const when = new Date(v.created_at).toLocaleString();
    return (
        <div
            className="px-4 py-3 flex items-start justify-between gap-3"
            data-testid="recent-visit-row"
        >
            <div className="min-w-0">
                <p className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#D4AF37] flex items-center gap-1.5">
                    {v.country_code ? (
                        <span className="bg-[#0A1220]/60 border border-[#D4AF37]/30 rounded px-1.5 py-0.5">
                            {v.country_code}
                        </span>
                    ) : null}
                    Visit
                </p>
                <p className="text-[#F1F5F9] text-base mt-1 break-words">
                    {v.location || "Unknown location"}
                    {v.path && v.path !== "/" ? (
                        <span className="font-mono-os text-xs text-[#94A3B8] ml-2">
                            · {v.path}
                        </span>
                    ) : null}
                </p>
            </div>
            <p className="font-mono-os text-xs text-[#94A3B8] flex-shrink-0 text-right">
                {when}
            </p>
        </div>
    );
}

function DailyChart({ series }) {
    const labelled = series.map((row) => ({
        day: row.day,
        ask: row.ask || 0,
        study: row.study_generated || 0,
        call: row.voice_call_started || 0,
        view: row.video_viewed || 0,
    }));
    const max = labelled.reduce(
        (m, r) => Math.max(m, r.ask, r.study, r.call, r.view),
        1
    );
    return (
        <div className="codex-card p-6">
            <h4 className="font-serif-display text-xl text-[#F9E596] mb-4">
                Last 30 days
            </h4>
            {labelled.length === 0 ? (
                <p className="text-[#CBD5E1] text-base">No activity yet.</p>
            ) : (
                <div className="space-y-4">
                    <div className="flex items-end gap-1 h-44">
                        {labelled.map((r) => (
                            <div
                                key={r.day}
                                className="flex-1 flex flex-col-reverse gap-px"
                                title={`${r.day}: ${r.ask} q · ${r.study} s · ${r.call} c · ${r.view} v`}
                            >
                                <span
                                    className="bg-[#D4AF37]/80"
                                    style={{ height: `${(r.ask / max) * 100}%` }}
                                />
                                <span
                                    className="bg-[#F9E596]/70"
                                    style={{ height: `${(r.study / max) * 100}%` }}
                                />
                                <span
                                    className="bg-[#4ADE80]/65"
                                    style={{ height: `${(r.call / max) * 100}%` }}
                                />
                                <span
                                    className="bg-[#9F7AEA]/55"
                                    style={{ height: `${(r.view / max) * 100}%` }}
                                />
                            </div>
                        ))}
                    </div>
                    <Legend />
                </div>
            )}
        </div>
    );
}

function Legend() {
    return (
        <div className="flex flex-wrap gap-3 text-xs font-mono-os uppercase tracking-[0.2em]">
            <span className="flex items-center gap-1.5 text-[#D4AF37]">
                <span className="block w-3 h-3 bg-[#D4AF37]/80 rounded-sm" /> Questions
            </span>
            <span className="flex items-center gap-1.5 text-[#F9E596]">
                <span className="block w-3 h-3 bg-[#F9E596]/70 rounded-sm" /> Studies
            </span>
            <span className="flex items-center gap-1.5 text-[#4ADE80]">
                <span className="block w-3 h-3 bg-[#4ADE80]/65 rounded-sm" /> Voice calls
            </span>
            <span className="flex items-center gap-1.5 text-[#9F7AEA]">
                <span className="block w-3 h-3 bg-[#9F7AEA]/55 rounded-sm" /> Video views
            </span>
        </div>
    );
}

function RecentRow({ ev }) {
    const label = labelForType(ev.type);
    const detail = detailFor(ev);
    const when = new Date(ev.created_at).toLocaleString();
    return (
        <div className="px-4 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#D4AF37]">
                    {label}
                </p>
                {detail && (
                    <p className="text-[#F1F5F9] text-base mt-1 break-words">{detail}</p>
                )}
            </div>
            <p className="font-mono-os text-xs text-[#94A3B8] flex-shrink-0 text-right">{when}</p>
        </div>
    );
}

function labelForType(t) {
    switch (t) {
        case "ask":
            return "Question";
        case "voice_call_started":
            return "Voice call";
        case "study_generated":
            return "Study generated";
        case "video_viewed":
            return "Video viewed";
        case "study_downloaded":
            return "Study downloaded";
        case "visit":
            return "Visit";
        default:
            return t.replace(/_/g, " ");
    }
}

function detailFor(ev) {
    const p = ev.payload || {};
    if (p.city || p.country) {
        return [p.city, p.region, p.country].filter(Boolean).join(", ");
    }
    return p.query || p.topic || p.title || "";
}
